import { useState } from "react";
import { useForm } from "react-hook-form";
import { GitBranch, Loader2, Plus } from "lucide-react";
import { useGitBranches, useCreateGitBranch } from "@/hooks/git";
import type { GitBranch as GitBranchType } from "@/services/git/types";
import { CustomSelect } from "@/components/atoms/CustomSelect";

function validateBranchName(name: string): string | true {
  if (/\s/.test(name)) return "Branch name cannot contain spaces.";
  if (name.startsWith(".")) return "Branch name cannot start with '.'.";
  if (name.endsWith(".")) return "Branch name cannot end with '.'.";
  if (name.endsWith(".lock")) return "Branch name cannot end with '.lock'.";
  if (name.includes("..")) return "Branch name cannot contain '..'.";
  if (name.startsWith("-")) return "Branch name cannot start with '-'.";
  if (name.endsWith("/")) return "Branch name cannot end with '/'.";
  if (name.includes("//")) return "Branch name cannot contain consecutive slashes.";
  if (name === "@") return "Branch name cannot be '@'.";
  if (name.includes("@{")) return "Branch name cannot contain '@{'.";
  if (/[~^:?*[\\\x00-\x1f\x7f]/.test(name)) return "Branch name cannot contain ~ ^ : ? * [ \\ or control characters.";
  return true;
}

// ─── Create Branch Pane ───────────────────────────────────────────────────────

export function CreateBranchPane({
  repoFullName,
  defaultBranch,
  onSuccess,
  onCancel,
}: {
  repoFullName: string;
  defaultBranch: string;
  onSuccess: (branch: GitBranchType) => void;
  onCancel: () => void;
}) {
  const [fromBranch, setFromBranch] = useState(defaultBranch);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: branches = [], isLoading: branchesLoading } = useGitBranches(repoFullName);
  const { mutate: create, isPending: creating } = useCreateGitBranch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ name: string }>({ mode: "onSubmit", reValidateMode: "onChange" });

  function onSubmit({ name }: { name: string }) {
    setApiError(null);
    create(
      { repo_full_name: repoFullName, name: name.trim(), from_branch: fromBranch },
      {
        onSuccess: (branch) => onSuccess(branch),
        onError: (err) => setApiError(err.message),
      },
    );
  }

  return (
    <div className="flex h-full flex-col p-5">
      <div className="mb-5">
        <div className="mb-1 flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">Create Branch</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Creates a new branch on GitHub and sets it as your default working branch.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium">Branch name</label>
          <input
            {...register("name", {
              required: "Branch name is required.",
              validate: validateBranchName,
              setValueAs: (v) => v.trim(),
            })}
            placeholder="feat/my-new-feature"
            autoFocus
            className={`w-full rounded-lg border bg-muted/30 px-3 py-2 font-mono text-xs outline-none placeholder:text-muted-foreground/50 focus:ring-1 ${errors.name ? "border-destructive/60 focus:border-destructive/60 focus:ring-destructive/20" : "border-border focus:border-primary/60 focus:ring-primary/20"}`}
          />
          {errors.name && (
            <p className="mt-1.5 text-xs text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium">Branch from</label>
          <CustomSelect
            value={fromBranch}
            onChange={setFromBranch}
            options={branches.map((b) => ({ value: b.name, label: b.name }))}
            disabled={branchesLoading}
            mono
          />
        </div>

        {apiError && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {apiError}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={creating}
            className="studio-button studio-button-primary flex-1 justify-center"
          >
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {creating ? "Creating…" : "Create Branch"}
          </button>
          <button type="button" onClick={onCancel} className="studio-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
